import { marked } from "marked";
import { SaveMessageToCosmos } from "./database/SaveMessageToCosmos";
import { applyCustomClasses } from "./applyCustomClasses";
import { config } from "../Config";

// Helper function to extract thinking and response content from Claude streams
const parseClaudeThinking = (content) => {
  const thinkingMatch = content.match(/\[THINKING\]([\s\S]*?)\[\/THINKING\]/);
  
  if (thinkingMatch) {
    const thinking = thinkingMatch[1].trim();
    const response = content
      .replace(/\[THINKING\][\s\S]*?\[\/THINKING\]\s*/g, "")
      .trim();
    return { thinking, response };
  }
  
  return { thinking: "", response: content };
};

// Helper function to create thinking HTML section
const createThinkingSection = (thinking) => {
  if (!thinking) return "";
  
  const escapedThinking = thinking
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  
  return `
    <details class="claude-thinking" style="margin-bottom: 1rem; padding: 0.75rem; background-color: #f5f5f5; border-radius: 0.5rem; border-left: 3px solid #9333ea;">
      <summary style="cursor: pointer; font-weight: 600; color: #666; user-select: none;">
        🧠 View Claude's Thinking
      </summary>
      <div style="margin-top: 0.75rem; color: #555; font-size: 0.95rem; white-space: pre-wrap; word-wrap: break-word;">
        ${escapedThinking}
      </div>
    </details>
  `;
};

const addCopyListeners = () => {
  const copyButtons = document.querySelectorAll(".copy-btn");
  copyButtons.forEach((btn) => {
    btn.addEventListener("click", (event) => {
      const codeElement = event.target.nextElementSibling.querySelector("code");
      const codeToCopy = codeElement.innerText;
      navigator.clipboard
        .writeText(codeToCopy)
        .then(() => {
          alert("Code copied to clipboard!");
        })
        .catch((err) => {});
    });
  });
};

const addPreviewListeners = () => {
  document.querySelectorAll(".doassist-preview-btn").forEach((btn) => {
    if (btn.dataset.listenerAdded) return;
    btn.dataset.listenerAdded = "true";
    btn.addEventListener("click", () => {
      const modal = document.getElementById(btn.dataset.cardId);
      if (modal) modal.style.display = "flex";
    });
  });
  document.querySelectorAll(".doassist-modal-close").forEach((btn) => {
    if (btn.dataset.listenerAdded) return;
    btn.dataset.listenerAdded = "true";
    btn.addEventListener("click", () => {
      const modal = document.getElementById(btn.dataset.cardId);
      if (modal) modal.style.display = "none";
    });
  });
  document.querySelectorAll(".doassist-preview-modal").forEach((modal) => {
    if (modal.dataset.listenerAdded) return;
    modal.dataset.listenerAdded = "true";
    modal.addEventListener("click", (e) => {
      if (e.target === modal) modal.style.display = "none";
    });
  });
};

export const GetStreamingResponse = async (
  userInputValue,
  suggestionKey,
  setMessages,
  setIsStreaming,
  abortControllerRef,
  scrollToBottom,
  fileInput = null,
  userDetails,
  chatId,
  selectedDocumentIds = null,
  selectedTools,
  instructions = "",
  extensionId = "DONA",
  selectedModel
) => {

  if (!userInputValue) {
    throw new Error("User input is empty");
  }

  const computedPluginOptions =
    selectedTools && selectedTools.length > 0
      ? {
          search_the_web_plugin_enabled: selectedTools.some(
            (t) => t.name === "Search the Web"
          ),
          generate_picture_plugin_enabled: selectedTools.some(
            (t) => t.name === "Generate Picture"
          ),
          search_documents_plugin_enabled: selectedTools.some(
            (t) => t.name === "Search Documents"
          ),
          graph_interpretation_plugin_enabled: selectedTools.some(
            (t) => t.name === "Graph Interpretation"
          ),
          generate_presentation_plugin_enabled: selectedTools.some(
            (t) => t.name === "Generate Presentation"
          ),
          generate_document_plugin_enabled: selectedTools.some(
            (t) => t.name === "Generate Document"
          ),
          generate_spreadsheet_plugin_enabled: selectedTools.some(
            (t) => t.name === "Generate Spreadsheet"
          )
        }
      : {
          search_the_web_plugin_enabled: false,
          generate_picture_plugin_enabled: false,
          search_documents_plugin_enabled: false,
          graph_interpretation_plugin_enabled: false,
          generate_presentation_plugin_enabled: false,
          generate_document_plugin_enabled: false,
          generate_spreadsheet_plugin_enabled: false,
        };

  setIsStreaming(true);

  let hasContent = false;
  setMessages((prevMessages) => [
    ...prevMessages,
    {
      type: "server",
      content: "",
      thinking: "",
      suggestionKey: suggestionKey,
      citations: "",
      isDefaultMessage: false,
      loading: true,
    }
  ]);

  abortControllerRef.current = new AbortController();
  const { signal } = abortControllerRef.current;

  let fullContentRef = "";
  let citationRef = "";
  let thinkingRef = "";

  // Configure marked options
  marked.setOptions({
    breaks: true,
    mangle: false,
    headerIds: false,
  });

  const handleStreamingContent = (chunk) => {
    if (chunk && chunk.trim() !== "[DONE]") {
      hasContent = true;
      fullContentRef += chunk;

      // Parse Claude thinking markers
      const { thinking, response } = parseClaudeThinking(fullContentRef);
      thinkingRef = thinking;

      let htmlContent;
      // If the tool is Graph Interpretation, do not run markdown processing.
      if (
        selectedTools &&
        Array.isArray(selectedTools) &&
        selectedTools.length === 1 &&
        selectedTools[0].name === "Graph Interpretation"
      ) {
        htmlContent = response;
      } else if (response.includes("file_download") && response.includes("download_url")) {
        // Handle PPT/DOCX file download response
        try {
          const jsonBlocks = [];
          let depth = 0, start = -1;
          for (let i = 0; i < response.length; i++) {
            if (response[i] === '{') { if (depth === 0) start = i; depth++; }
            if (response[i] === '}') { depth--; if (depth === 0 && start >= 0) {
              try { const p = JSON.parse(response.substring(start, i + 1)); if (p.type === "file_download") jsonBlocks.push(p); } catch(e) {}
              start = -1;
            }}
          }
          let downloadCards = "";
          for (const parsed of jsonBlocks) {
            const downloadUrl = parsed.download_url;
            const previewUrl = parsed.preview_url || downloadUrl;
            const filename = parsed.filename;
            const fileType = parsed.file_type === "pptx" ? "Presentation" : parsed.file_type === "xlsx" ? "Spreadsheet" : "Document";
            const icon = parsed.file_type === "pptx" ? "\uD83D\uDCCA" : parsed.file_type === "xlsx" ? "\uD83D\uDCCA" : "\uD83D\uDCC4";
            const content = parsed.content || {};
            const cardId = `file-card-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
            // preview_url from backend is already a PDF (generated alongside the PPTX/DOCX)
            // Browser native PDF viewer = instant load, no Microsoft branding, perfect quality
            const previewIframeUrl = previewUrl.toLowerCase().endsWith('.pdf')
              ? `${previewUrl}#toolbar=1&navpanes=0`
              : `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;

            let summaryText = "";
            if (parsed.file_type === "pptx" && content && content.slides) summaryText = `${content.slides.length} slides`;
            else if (parsed.file_type === "docx" && content && content.sections) summaryText = `${content.sections.length} sections`;
            else if (parsed.file_type === "xlsx" && content && content.sheets) summaryText = `${content.sheets.length} sheets`;

            downloadCards += `
              <div style="padding: 1rem; background: #f8f8ff; border-radius: 10px; border-left: 4px solid #333366; margin: 0.5rem 0;">
                <p style="margin: 0 0 0.3rem 0; font-weight: 600; color: #333366; font-size: 1rem;">${icon} ${fileType} Generated Successfully</p>
                <p style="margin: 0 0 0.2rem 0; color: #555; font-size: 0.9rem;">${content.title || filename}</p>
                ${summaryText ? `<p style="margin: 0 0 0.5rem 0; color: #999; font-size: 0.8rem;">${summaryText}</p>` : ''}
                <div style="display: flex; gap: 10px; margin-top: 8px;">
                  <button class="doassist-preview-btn" data-card-id="${cardId}"
                    style="background: white; color: #333366; border: 2px solid #333366; padding: 8px 20px; border-radius: 6px; font-weight: 600; font-size: 0.9rem; cursor: pointer;">
                    Preview ${fileType}
                  </button>
                  <a href="${downloadUrl}" download="${filename}" target="_blank"
                    style="display: inline-block; background: #333366; color: white; padding: 8px 20px; border-radius: 6px; text-decoration: none; font-weight: 600; font-size: 0.9rem;">
                    Download ${fileType}
                  </a>
                </div>
              </div>
              <div id="${cardId}" class="doassist-preview-modal" style="display:none; position:fixed; top:0; left:0; width:100%; height:100%; background:rgba(0,0,0,0.5); z-index:9999; justify-content:center; align-items:center;">
                <div style="background:white; width:92%; max-width:960px; height:90vh; border-radius:12px; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 8px 32px rgba(0,0,0,0.25);">
                  <div style="background:#333366; color:white; padding:14px 20px; display:flex; justify-content:space-between; align-items:center; flex-shrink:0;">
                    <div style="display:flex; align-items:center; gap:10px;">
                      <span style="font-size:1.3rem;">${icon}</span>
                      <div>
                        <div style="font-size:1.1rem; font-weight:700;">${content.title || filename}</div>
                        ${content.subtitle ? `<div style="color:#99cc33; font-size:0.8rem; margin-top:1px;">${content.subtitle}</div>` : ''}
                      </div>
                    </div>
                    <button class="doassist-modal-close" data-card-id="${cardId}" style="background:none; border:none; color:white; font-size:1.8rem; cursor:pointer; padding:0 6px; line-height:1;">&times;</button>
                  </div>
                  <div style="flex:1; background:#f0f0f0;">
                    <iframe src="${previewIframeUrl}" style="width:100%; height:100%; border:none;" allowfullscreen="true"></iframe>
                  </div>
                  <div style="padding:10px 20px; border-top:1px solid #eee; display:flex; justify-content:flex-end; gap:10px; flex-shrink:0; background:white;">
                    <button class="doassist-modal-close" data-card-id="${cardId}" style="background:white; color:#333366; border:2px solid #333366; padding:8px 20px; border-radius:6px; font-weight:600; font-size:0.9rem; cursor:pointer;">Close</button>
                    <a href="${downloadUrl}" download="${filename}" target="_blank" style="display:inline-block; background:#333366; color:white; padding:8px 20px; border-radius:6px; text-decoration:none; font-weight:600; font-size:0.9rem;">Download</a>
                  </div>
                </div>
              </div>`;
          }
          htmlContent = downloadCards || marked.parse(response);
        } catch (error) {
          htmlContent = marked.parse(response);
          htmlContent = applyCustomClasses(htmlContent);
        }
      } else if (
        selectedTools &&
        Array.isArray(selectedTools) &&
        selectedTools.length === 1 &&
        selectedTools[0].name === "Generate Picture" &&
        chunk.includes("image_url")
      ) {
        try {
          const parsedChunk = JSON.parse(chunk);
          const imageUrl = parsedChunk.image_url;
          htmlContent = `
            <div class="custom-image-wrapper">
              <img src="${imageUrl}" alt="Generated Image" class="custom-generated-image"/>
              <div class="custom-download-icon" title="Download Image" aria-label="Download Image"></div>
            </div>`;
        } catch (error) {
          htmlContent = marked.parse(response);
          htmlContent = applyCustomClasses(htmlContent);
        }
      } else {
        htmlContent = marked.parse(response);
        htmlContent = applyCustomClasses(htmlContent);
      }

      // Prepend thinking section if available
      const thinkingHtml = createThinkingSection(thinkingRef);
      const finalHtml = thinkingHtml + htmlContent;

      setMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage.type === "user") {
          newMessages.push({
            type: "server",
            content: finalHtml,
            thinking: thinkingRef,
            suggestionKey: suggestionKey,
            citations: citationRef,
            isDefaultMessage: false,
            loading: true,
          });
        } else if (lastMessage.type === "server") {
          lastMessage.content = finalHtml;
          lastMessage.thinking = thinkingRef;
          lastMessage.citations = citationRef || lastMessage.citations;
        }
        return newMessages;
      });

      addCopyListeners();
      addPreviewListeners();
      if (typeof scrollToBottom === "function") {
        scrollToBottom();
      }
    }
  };

  const streamResponse = async (prompt) => {
    const formData = new FormData();
    formData.append("user_input", prompt);
    formData.append("chat_id", chatId || "default");
    formData.append("instructions", instructions);
    formData.append("plugin_options", JSON.stringify(computedPluginOptions));
    const toolNames = selectedTools.map((t) => t.name);
    formData.append("tool_name", JSON.stringify(toolNames));
    formData.append("file_id", JSON.stringify(selectedDocumentIds || []));
    formData.append("extension_id", extensionId);
    formData.append("models", selectedModel);

    if (fileInput) {
      formData.append("file", fileInput);
    }

    // Route all requests through rag_function, except Generate Picture which requires DALL-E (GPT only)
    let uri = config.ragFunction;
    if (
      selectedTools &&
      selectedTools.length === 1 &&
      selectedTools[0].name === "Generate Picture"
    ) {
      uri = config.generatePicture;
    }

    const response = await fetch(uri, {
      method: "POST",
      body: formData,
      signal,
    });

    if (!response.ok) {
      const errorResponse = await response.json();
      if (
        errorResponse.error?.inner_error?.code === "ResponsibleAIPolicyViolation"
      ) {
        const revisedPrompt =
          errorResponse.error.inner_error.revised_prompt || prompt;
        return await streamResponse(revisedPrompt);
      }
      throw new Error("Network response was not ok");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      handleStreamingContent(chunk);
    }
  };

  try {
    await streamResponse(userInputValue);
    if (hasContent) {     
      const updatedTitle = await SaveMessageToCosmos(
        userDetails,
        userInputValue,
        fullContentRef,
        chatId,
        citationRef,
        thinkingRef,
        selectedModel
      );
      return updatedTitle;
    }
  } catch (error) {
    setIsStreaming(false);
    throw error;
  } finally {
    setIsStreaming(false);
    setMessages((prevMessages) => {
      if (!hasContent) {
        return prevMessages.slice(0, -1);
      }
      return prevMessages.map((msg) => ({ ...msg, loading: false }));
    });
  }
};
